import {assertPartialSchema, createAction, useSelector, v} from '../../../lib'
import {MultiDayTimeSlotWithRecord, TaskTimeSlotCombination} from '../../../shared/mongo'
import {mergeUniqueItems, toHydratedStringMap, toSerializableStringMap, toSerializableValue} from '../../serializable'

export enum ProjectDataDigestActionType {
  PROJECT_DATA_DIGEST_SET = 'ADHERENCE_PROJECT_DATA_DIGEST_SET',
}

export const doPROJECT_DATA_DIGEST_SET = createAction(ProjectDataDigestActionType.PROJECT_DATA_DIGEST_SET)

interface ProjectDataDigestState {
  [projectId: string]: SerializableProjectDataDigest
}

export interface SerializableProjectDataDigest {
  projectId: string
  coveredYYMMIndex: number[]
  participantDataDigestMap: SerializableParticipantDataDigestMap
}

export interface ProjectDataDigest {
  projectId: string
  coveredYYMMIndex: number[]
  participantDataDigestMap: ParticipantDataDigestMap
}

export type ParticipantDataDigestMap = Record<string, ParticipantDataDigest>
type SerializableParticipantDataDigestMap = Record<string, SerializableParticipantDataDigest>

interface SerializableParticipantDataDigest extends Omit<ParticipantDataDigest, 'taskMultiDayTimeSlotMap'> {
  taskMultiDayTimeSlotMap?: SerializableTaskMultiDayTimeSlotMap
}

export interface ParticipantDataDigest {
  taskDataDates?: Record<string, number[]>
  taskCompletionDateCountMap?: Record<string, Record<string, number>>
  taskTimeSlotCombination?: TaskTimeSlotCombination
  taskMultiDayTimeSlotMap?: TaskMultiDayTimeSlotMap
  garminDeviceDataDates?: number[]
  garminConnectDataDates?: number[]
  dexcomDataDates?: number[]
  garminDeviceDataDatesByType?: Record<string, number[]>
  garminDirectBbiDataDigest?: BbiDataDigestDateMap
  garminConnectWearTimeDateMap?: GarminConnectWearTimeDateMap
}

export type BbiDataDigestDateMap = Record<string, GarminBbiDigest>
export type ParticipantBbiDataDigestMap = Record<string, BbiDataDigestDateMap>
export type GarminConnectWearTimeDateMap = Record<string, number>
export type ParticipantGarminConnectWearTimeMap = Record<string, GarminConnectWearTimeDateMap>

export interface GarminBbiDigest {
  wearTime: number
}

export type TaskMultiDayTimeSlotMap = {[taskId: string]: MultiDayTimeSlotMap}
export type MultiDayTimeSlotMap = Map<string, MultiDayTimeSlotWithRecord>

type SerializableTaskMultiDayTimeSlotMap = {[taskId: string]: SerializableMultiDayTimeSlotMap}
type SerializableMultiDayTimeSlotMap = Record<string, MultiDayTimeSlotWithRecord>

export interface SelectedCellIndex {
  participantId: string
  yymmddIndex: number
}

interface RootState {
  projectDataDigest: ProjectDataDigestState
}

let cachedProjectDataDigestStateRaw: ProjectDataDigestState | null = null
let cachedProjectDataDigestStateHydrated: {[projectId: string]: ProjectDataDigest} = {}

/* selector */
export const selectProjectDataDigest = () => {
  return useSelector((state: RootState) => hydrateProjectDataDigestState(state.projectDataDigest))
}

export const projectDataDigestActionCreators = {
  doPROJECT_DATA_DIGEST_SET,
}

export const projectDataDigestDefaultState: ProjectDataDigestState = {}

type Action = {
  type: ProjectDataDigestActionType.PROJECT_DATA_DIGEST_SET
  payload: {
    projectDataDigest: {
      projectId: string
      coveredYYMMIndex: number[]
      participantDataDigestMap: {
        [participantId: string]: object
      }
    }
  }
}

export const projectDataDigestReducer = (
  state = {...projectDataDigestDefaultState},
  {type, payload}: Action,
): ProjectDataDigestState => {
  switch (type) {
    case ProjectDataDigestActionType.PROJECT_DATA_DIGEST_SET:
      assertPartialSchema({
        payload,
        schema: v.object({
          projectDataDigest: v.object({
            projectId: v.string().exist(),
            coveredYYMMIndex: v.array().items(v.number()).exist(),
            participantDataDigestMap: v.object().exist(),
          }),
        }),
      })
      return updateProjectDataDigestInState(state, payload.projectDataDigest as ProjectDataDigest)

    default:
      return state
  }
}

export const hydrateProjectDataDigestState = (
  projectDataDigestState: ProjectDataDigestState,
): {[projectId: string]: ProjectDataDigest} => {
  if (cachedProjectDataDigestStateRaw === projectDataDigestState) {
    return cachedProjectDataDigestStateHydrated
  }

  const hydratedState = Object.entries(projectDataDigestState).reduce(
    (result, [projectId, projectDataDigest]) => {
      return {
        ...result,
        [projectId]: hydrateProjectDataDigest(projectDataDigest) as ProjectDataDigest,
      }
    },
    {} as {[projectId: string]: ProjectDataDigest},
  )

  cachedProjectDataDigestStateRaw = projectDataDigestState
  cachedProjectDataDigestStateHydrated = hydratedState

  return hydratedState
}

export const hydrateProjectDataDigest = (
  projectDataDigest: SerializableProjectDataDigest | undefined,
): ProjectDataDigest | undefined => {
  if (!projectDataDigest) {
    return undefined
  }

  const participantDataDigestMap = Object.entries(projectDataDigest.participantDataDigestMap).reduce(
    (result, [participantId, participantDataDigest]) => {
      return {
        ...result,
        [participantId]: hydrateParticipantDataDigest(participantDataDigest),
      }
    },
    {} as ParticipantDataDigestMap,
  )

  return {
    ...projectDataDigest,
    participantDataDigestMap,
  }
}

function hydrateParticipantDataDigest(participantDataDigest: SerializableParticipantDataDigest): ParticipantDataDigest {
  const taskMultiDayTimeSlotMap = participantDataDigest.taskMultiDayTimeSlotMap
    ? Object.entries(participantDataDigest.taskMultiDayTimeSlotMap).reduce((result, [taskId, multiDayTimeSlotMap]) => {
        return {
          ...result,
          [taskId]:
            (toHydratedStringMap(multiDayTimeSlotMap) as MultiDayTimeSlotMap | undefined) ??
            new Map<string, MultiDayTimeSlotWithRecord>(),
        }
      }, {} as TaskMultiDayTimeSlotMap)
    : undefined

  return {
    ...participantDataDigest,
    taskMultiDayTimeSlotMap,
  }
}

function updateProjectDataDigestInState(
  state: ProjectDataDigestState,
  inputProjectDataDigest: ProjectDataDigest | SerializableProjectDataDigest,
) {
  const normalisedProjectDataDigest = normaliseProjectDataDigest(inputProjectDataDigest)
  const projectId = normalisedProjectDataDigest.projectId
  const existingProjectDataDigest = state[projectId]

  if (existingProjectDataDigest) {
    if (isSubset(existingProjectDataDigest.coveredYYMMIndex, normalisedProjectDataDigest.coveredYYMMIndex)) {
      return state
    }

    return {
      ...state,
      [projectId]: {
        ...existingProjectDataDigest,
        coveredYYMMIndex: mergeUniqueItems(
          existingProjectDataDigest.coveredYYMMIndex,
          normalisedProjectDataDigest.coveredYYMMIndex,
        ) as number[],
        participantDataDigestMap: mergeParticipantDataDigestMap(
          existingProjectDataDigest.participantDataDigestMap,
          normalisedProjectDataDigest.participantDataDigestMap,
        ),
      },
    }
  }

  return {
    ...state,
    [projectId]: buildTaskMultiDayTimeSlotMap(normalisedProjectDataDigest),
  }
}

function normaliseProjectDataDigest(
  projectDataDigest: ProjectDataDigest | SerializableProjectDataDigest,
): SerializableProjectDataDigest {
  const participantDataDigestMap = Object.entries(projectDataDigest.participantDataDigestMap).reduce(
    (result, [participantId, participantDataDigest]) => {
      return {
        ...result,
        [participantId]: normaliseParticipantDataDigest(participantDataDigest),
      }
    },
    {} as SerializableParticipantDataDigestMap,
  )

  return {
    ...(toSerializableValue(projectDataDigest) as SerializableProjectDataDigest),
    participantDataDigestMap,
  }
}

function normaliseParticipantDataDigest(
  participantDataDigest: ParticipantDataDigest | SerializableParticipantDataDigest,
): SerializableParticipantDataDigest {
  const serializableTaskMultiDayTimeSlotMap = participantDataDigest.taskMultiDayTimeSlotMap
    ? Object.entries(participantDataDigest.taskMultiDayTimeSlotMap).reduce((result, [taskId, multiDayTimeSlotMap]) => {
        return {
          ...result,
          [taskId]:
            (toSerializableStringMap(multiDayTimeSlotMap as MultiDayTimeSlotMap | SerializableMultiDayTimeSlotMap) as
              | SerializableMultiDayTimeSlotMap
              | undefined) ?? {},
        }
      }, {} as SerializableTaskMultiDayTimeSlotMap)
    : undefined

  return {
    ...(toSerializableValue(participantDataDigest) as SerializableParticipantDataDigest),
    taskMultiDayTimeSlotMap: serializableTaskMultiDayTimeSlotMap,
  }
}

function buildTaskMultiDayTimeSlotMap(projectDataDigest: SerializableProjectDataDigest): SerializableProjectDataDigest {
  const participantDataDigestMap = Object.entries(projectDataDigest.participantDataDigestMap).reduce(
    (result, [participantId, participantDigest]) => {
      const nextParticipantDigest = {...participantDigest}
      if (nextParticipantDigest.taskTimeSlotCombination) {
        for (const [taskId, timeSlotCombination] of Object.entries(nextParticipantDigest.taskTimeSlotCombination)) {
          if (timeSlotCombination.multiDayTimeSlotList) {
            const multiDayTimeSlotMap = timeSlotCombination.multiDayTimeSlotList.reduce((slotResult, timeSlot) => {
              return {
                ...slotResult,
                [timeSlot.id]: toSerializableValue(timeSlot) as MultiDayTimeSlotWithRecord,
              }
            }, {} as SerializableMultiDayTimeSlotMap)

            nextParticipantDigest.taskMultiDayTimeSlotMap = {
              [taskId]: multiDayTimeSlotMap,
              ...nextParticipantDigest.taskMultiDayTimeSlotMap,
            }
          }
        }
      }

      return {
        ...result,
        [participantId]: nextParticipantDigest,
      }
    },
    {} as SerializableParticipantDataDigestMap,
  )

  return {
    ...projectDataDigest,
    participantDataDigestMap,
  }
}

const isSubset = (parent: number[], children: number[]) => {
  if (parent) {
    return children.every((item) => {
      return parent.includes(item)
    })
  }
  return false
}

function mergeParticipantDataDigestMap(
  d1: SerializableParticipantDataDigestMap,
  d2: SerializableParticipantDataDigestMap,
): SerializableParticipantDataDigestMap {
  const merged: SerializableParticipantDataDigestMap = {...d1}

  for (const key in d2) {
    if (key in merged) {
      const mergedParticipantDataDigest = {
        ...merged[key],
      }
      mergedParticipantDataDigest.taskDataDates = mergeDataDatesRecord(
        mergedParticipantDataDigest.taskDataDates,
        d2[key].taskDataDates,
      )
      mergedParticipantDataDigest.taskCompletionDateCountMap = mergeDatesDataCountMap(
        mergedParticipantDataDigest.taskCompletionDateCountMap,
        d2[key].taskCompletionDateCountMap,
      )
      mergedParticipantDataDigest.garminDeviceDataDatesByType = mergeDataDatesRecord(
        mergedParticipantDataDigest.garminDeviceDataDatesByType,
        d2[key].garminDeviceDataDatesByType,
      )
      mergedParticipantDataDigest.garminDeviceDataDates = mergeDataDates(
        mergedParticipantDataDigest.garminDeviceDataDates,
        d2[key].garminDeviceDataDates,
      )
      mergedParticipantDataDigest.garminConnectDataDates = mergeDataDates(
        mergedParticipantDataDigest.garminConnectDataDates,
        d2[key].garminConnectDataDates,
      )
      mergedParticipantDataDigest.dexcomDataDates = mergeDataDates(
        mergedParticipantDataDigest.dexcomDataDates,
        d2[key].dexcomDataDates,
      )
      mergedParticipantDataDigest.taskMultiDayTimeSlotMap = mergeTaskMultiDayTimeSlotMap(
        mergedParticipantDataDigest.taskMultiDayTimeSlotMap,
        d2[key].taskMultiDayTimeSlotMap,
      )
      merged[key] = mergedParticipantDataDigest
    } else {
      merged[key] = normaliseParticipantDataDigest(d2[key])
    }
  }

  return merged
}

function mergeTaskMultiDayTimeSlotMap(
  d1?: SerializableTaskMultiDayTimeSlotMap,
  d2?: SerializableTaskMultiDayTimeSlotMap,
): SerializableTaskMultiDayTimeSlotMap | undefined {
  if (d1 && d2) {
    const merged: SerializableTaskMultiDayTimeSlotMap = {...d1}
    for (const taskId in d2) {
      merged[taskId] = {
        ...merged[taskId],
        ...d2[taskId],
      }
    }
    return merged
  }
  return d1 || d2
}

function mergeDataDatesRecord(
  d1?: Record<string, number[]>,
  d2?: Record<string, number[]>,
): Record<string, number[]> | undefined {
  if (d1 && d2) {
    const merged: Record<string, number[]> = {...d1}
    for (const key in d2) {
      if (key in merged) {
        const mergedDates = mergeDataDates(merged[key], d2[key])
        if (mergedDates) {
          merged[key] = mergedDates
        }
      } else {
        merged[key] = d2[key]
      }
    }
    return merged
  } else {
    return d1 || d2
  }
}

function mergeDataDates(d1?: number[], d2?: number[]): number[] | undefined {
  return mergeUniqueItems(d1, d2)
}

function mergeDatesDataCountMap(
  d1?: Record<string, Record<string, number>>,
  d2?: Record<string, Record<string, number>>,
): Record<string, Record<string, number>> | undefined {
  if (d1 && d2) {
    const merged: Record<string, Record<string, number>> = {...d1}
    for (const key in d2) {
      if (key in merged) {
        merged[key] = {
          ...merged[key],
          ...d2[key],
        }
      } else {
        merged[key] = d2[key]
      }
    }
    return merged
  } else {
    return d1 || d2
  }
}
